import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Calendar,
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  FileText,
  Filter,
  Inbox,
  Linkedin,
  Loader2,
  RefreshCw,
  Search,
  Send,
  UserPlus,
  X,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBanner } from "@/components/ui/status-banner";
import { cn } from "@/lib/cn";
import {
  deriveStatus,
  statusLabel,
  statusVariant,
  type AssignmentStatus,
} from "@/lib/assignmentStatus";
import { resendInvitation } from "@/lib/invitations";
import { useActiveClientId } from "@/hooks/useActiveClient";
import { NoClientPicked } from "@/components/NoClientPicked";
import { supabase } from "@/lib/supabase";
import {
  APPLICATION_STATUS_LABELS,
  type Application,
  type ApplicationStatus,
  type Assignment,
  type Company,
  type Exercise,
  type Search as JobSearch,
} from "@/lib/types";

export const Route = createFileRoute("/invitations")({
  component: InvitationsPage,
});

function InvitationsPage() {
  return (
    <LoginGate
      title="DeepDive"
      subtitle="Iniciá sesión para ver invitaciones."
    >
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <InvitationsContent />
        </AppShell>
      )}
    </LoginGate>
  );
}

type DateRange = "all" | "today" | "7d" | "30d";

function InvitationsContent() {
  const [activeClientId] = useActiveClientId();
  if (!activeClientId) {
    return (
      <NoClientPicked
        title="Elegí un cliente para ver sus invitaciones"
        description="Cada cliente tiene sus propias invitaciones y postulaciones. Elegí uno desde el dropdown de arriba a la izquierda."
      />
    );
  }
  return (
    <div className="h-full flex flex-col px-6 py-5 overflow-hidden">
      <div className="flex items-end justify-between mb-5 shrink-0">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Invitaciones
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Tests enviados y postulaciones recibidas vía link público.
          </p>
        </div>
        <Button variant="primary" asChild>
          <Link to="/generate">
            <Send className="h-4 w-4" />
            Nueva invitación
          </Link>
        </Button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <InvitationsPanel />
        <ApplicationsPanel />
      </div>
    </div>
  );
}

// =================== INVITATIONS PANEL ===================
function InvitationsPanel() {
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [exerciseFilter, setExerciseFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | "all">("all");
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    title: string;
    detail?: string;
  } | null>(null);
  const qc = useQueryClient();
  const [activeClientId] = useActiveClientId();

  const { data: exercises = [] } = useQuery({
    queryKey: ["exercises", "for-filter", { activeClientId }],
    queryFn: async () => {
      let q = supabase
        .from("exercises")
        .select("id, title, language")
        .order("title");
      if (activeClientId) q = q.eq("company_id", activeClientId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Pick<Exercise, "id" | "title" | "language">[];
    },
  });

  const exerciseMap = useMemo(
    () => Object.fromEntries(exercises.map((e) => [e.id, e])),
    [exercises]
  );

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ["assignments", { dateRange, exerciseFilter, activeClientId }],
    queryFn: async (): Promise<Assignment[]> => {
      let q = supabase
        .from("assignments")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);

      const since = sinceFromRange(dateRange);
      if (since) q = q.gte("created_at", since.toISOString());

      if (exerciseFilter.length > 0) {
        q = q.overlaps("exercise_ids", exerciseFilter);
      }

      if (activeClientId) {
        q = q.eq("company_id", activeClientId);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Assignment[];
    },
  });

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return assignments.filter((a) => {
      if (statusFilter !== "all" && deriveStatus(a) !== statusFilter) return false;
      if (!t) return true;
      return (
        a.candidate_name.toLowerCase().includes(t) ||
        a.candidate_email.toLowerCase().includes(t) ||
        a.id.toLowerCase().startsWith(t)
      );
    });
  }, [assignments, search, statusFilter]);

  const resendMutation = useMutation({
    mutationFn: (assignmentId: string) => resendInvitation(assignmentId),
    onSuccess: () => {
      setFeedback({ kind: "success", title: "Email reenviado" });
      qc.invalidateQueries({ queryKey: ["assignments"] });
    },
    onError: (e: Error) => {
      setFeedback({
        kind: "error",
        title: "No se pudo reenviar",
        detail: e.message,
      });
    },
  });

  const hasFilters =
    exerciseFilter.length > 0 ||
    statusFilter !== "all" ||
    dateRange !== "30d" ||
    !!search;

  return (
    <Panel
      title="Invitaciones enviadas"
      icon={Send}
      count={filtered.length}
      totalCount={assignments.length}
    >
      {feedback && (
        <div className="px-3 pt-3 shrink-0">
          <StatusBanner
            variant={feedback.kind}
            title={feedback.title}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.detail}
          </StatusBanner>
        </div>
      )}

      <div className="p-3 border-b border-border shrink-0 flex flex-wrap gap-1.5">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Buscar nombre, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
        <ExerciseFilter
          exercises={exercises}
          value={exerciseFilter}
          onChange={setExerciseFilter}
        />
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setExerciseFilter([]);
              setStatusFilter("all");
              setDateRange("30d");
              setSearch("");
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <InvitationsEmptyState />
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((a) => (
              <InvitationRow
                key={a.id}
                assignment={a}
                exerciseMap={exerciseMap}
                onResend={() => resendMutation.mutate(a.id)}
                isResending={
                  resendMutation.isPending && resendMutation.variables === a.id
                }
              />
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

// =================== APPLICATIONS PANEL ===================
function ApplicationsPanel() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "all">(
    "all"
  );
  const [activeClientId] = useActiveClientId();

  const { data: applications = [], isLoading } = useQuery({
    queryKey: ["applications", { activeClientId }],
    queryFn: async (): Promise<Application[]> => {
      let q = supabase
        .from("applications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);
      if (activeClientId) q = q.eq("company_id", activeClientId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Application[];
    },
  });

  const searchIds = useMemo(
    () => Array.from(new Set(applications.map((a) => a.search_id))),
    [applications]
  );
  const companyIds = useMemo(
    () => Array.from(new Set(applications.map((a) => a.company_id))),
    [applications]
  );

  const { data: searchMap = {} } = useQuery({
    queryKey: ["apps-searches", searchIds],
    enabled: searchIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("searches")
        .select("id, puesto")
        .in("id", searchIds);
      if (error) throw error;
      const m: Record<string, Pick<JobSearch, "id" | "puesto">> = {};
      for (const s of (data ?? []) as Pick<JobSearch, "id" | "puesto">[]) {
        m[s.id] = s;
      }
      return m;
    },
  });

  const { data: companyMap = {} } = useQuery({
    queryKey: ["apps-companies", companyIds],
    enabled: companyIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name")
        .in("id", companyIds);
      if (error) throw error;
      const m: Record<string, Pick<Company, "id" | "name">> = {};
      for (const c of (data ?? []) as Pick<Company, "id" | "name">[]) {
        m[c.id] = c;
      }
      return m;
    },
  });

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return applications.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (!t) return true;
      return (
        a.candidate_name.toLowerCase().includes(t) ||
        a.candidate_email.toLowerCase().includes(t)
      );
    });
  }, [applications, search, statusFilter]);

  const hasFilters = statusFilter !== "all" || !!search;

  return (
    <Panel
      title="Postulaciones"
      icon={UserPlus}
      count={filtered.length}
      totalCount={applications.length}
    >
      <div className="p-3 border-b border-border shrink-0 flex flex-wrap gap-1.5">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Buscar nombre, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <ApplicationStatusFilter
          value={statusFilter}
          onChange={setStatusFilter}
        />
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStatusFilter("all");
              setSearch("");
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <ApplicationsEmptyState />
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((a) => (
              <ApplicationRow
                key={a.id}
                application={a}
                searchTitle={searchMap[a.search_id]?.puesto}
                companyName={companyMap[a.company_id]?.name}
              />
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

// =================== PANEL SHELL ===================
function Panel({
  title,
  icon: Icon,
  count,
  totalCount,
  children,
}: {
  title: string;
  icon: typeof Send;
  count: number;
  totalCount: number;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-[var(--radius)] border border-border bg-card overflow-hidden min-h-0">
      <header className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        </div>
        <span className="text-[11px] text-muted-foreground font-mono tabular-nums">
          {count === totalCount
            ? `${totalCount}`
            : `${count} / ${totalCount}`}
        </span>
      </header>
      {children}
    </section>
  );
}

// =================== ROWS ===================
function ApplicationRow({
  application: a,
  searchTitle,
  companyName,
}: {
  application: Application;
  searchTitle: string | undefined;
  companyName: string | undefined;
}) {
  const [cvLoading, setCvLoading] = useState(false);

  async function openCv() {
    if (!a.cv_url) return;
    setCvLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from("cvs")
        .createSignedUrl(a.cv_url, 60);
      if (error) throw error;
      if (data?.signedUrl) {
        window.open(data.signedUrl, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      console.error("CV signed url error", e);
    } finally {
      setCvLoading(false);
    }
  }

  return (
    <li className="group hover:bg-accent/40 transition-colors">
      <div className="flex items-start gap-3 px-3 py-3">
        <Badge
          variant={applicationStatusVariant(a.status)}
          className="shrink-0 w-20 justify-center text-center mt-0.5"
        >
          {APPLICATION_STATUS_LABELS[a.status]}
        </Badge>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">
              {a.candidate_name}
            </span>
            <span className="text-[11px] text-muted-foreground truncate">
              {a.candidate_email}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {searchTitle ?? "Búsqueda eliminada"}
            {companyName && ` · ${companyName}`}
          </div>
          {a.cover_letter && (
            <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">
              {a.cover_letter}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {formatDateShort(a.created_at)}
          </span>
          <div className="flex items-center gap-0.5">
            {a.linkedin_url && (
              <a
                href={a.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                title="Ver LinkedIn"
              >
                <Linkedin className="h-3 w-3" />
              </a>
            )}
            {a.cv_url && (
              <button
                type="button"
                onClick={openCv}
                disabled={cvLoading}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                title="Descargar CV"
              >
                {cvLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <FileText className="h-3 w-3" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function applicationStatusVariant(
  status: ApplicationStatus
): "primary" | "info" | "success" | "warning" | "danger" | "muted" {
  switch (status) {
    case "new":
      return "primary";
    case "reviewing":
      return "info";
    case "test_sent":
      return "warning";
    case "hired":
      return "success";
    case "rejected":
      return "danger";
    default:
      return "muted";
  }
}

interface InvitationRowProps {
  assignment: Assignment;
  exerciseMap: Record<string, Pick<Exercise, "title" | "language">>;
  onResend: () => void;
  isResending: boolean;
}

function InvitationRow({
  assignment: a,
  exerciseMap,
  onResend,
  isResending,
}: InvitationRowProps) {
  const [copied, setCopied] = useState(false);
  const status = deriveStatus(a);
  const variant = statusVariant(status);
  const testUrl = `${window.location.origin}/?a=${a.id}`;

  function copy() {
    navigator.clipboard.writeText(testUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const canResend = !a.completed_at && !a.invalidated_at;

  return (
    <li className="group hover:bg-accent/40 transition-colors">
      <Link
        to="/invitations/$assignmentId"
        params={{ assignmentId: a.id }}
        className="flex items-start gap-3 px-3 py-3"
      >
        <Badge variant={variant} className="shrink-0 w-20 justify-center text-center mt-0.5">
          {statusLabel(status)}
        </Badge>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">
              {a.candidate_name}
            </span>
            <span className="text-[11px] text-muted-foreground truncate">
              {a.candidate_email}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {a.exercise_ids
              .map((id) => exerciseMap[id]?.title ?? id.slice(0, 8))
              .join(" · ")}
          </div>
        </div>

        <div
          className="flex flex-col items-end gap-1 shrink-0"
          onClick={(e) => e.preventDefault()}
        >
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {formatDateShort(a.created_at)}
          </span>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                copy();
              }}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
              title="Copiar link"
            >
              {copied ? (
                <Check className="h-3 w-3 text-success" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
            {canResend && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onResend();
                }}
                disabled={isResending}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                title={a.sent_email_at ? "Reenviar email" : "Enviar email"}
              >
                {isResending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
              </button>
            )}
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-50 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </Link>
    </li>
  );
}

// =================== EMPTY STATES ===================
function InvitationsEmptyState() {
  return (
    <div className="py-16 text-center px-6">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 border border-primary/30 mb-4">
        <Inbox className="h-5 w-5 text-primary" />
      </div>
      <h3 className="text-sm font-semibold tracking-tight mb-1">
        Ninguna invitación todavía
      </h3>
      <p className="text-xs text-muted-foreground mb-5">
        Cuando envíes tu primera invitación va a aparecer acá.
      </p>
      <Button variant="primary" size="sm" asChild>
        <Link to="/generate">
          <Send className="h-4 w-4" />
          Nueva invitación
        </Link>
      </Button>
    </div>
  );
}

function ApplicationsEmptyState() {
  return (
    <div className="py-16 text-center px-6">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 border border-primary/30 mb-4">
        <UserPlus className="h-5 w-5 text-primary" />
      </div>
      <h3 className="text-sm font-semibold tracking-tight mb-1">
        Sin postulaciones todavía
      </h3>
      <p className="text-xs text-muted-foreground mb-5">
        Publicá una búsqueda en LinkedIn con el link de postulación.
      </p>
      <Button variant="default" size="sm" asChild>
        <Link to="/clients">
          <ExternalLink className="h-4 w-4" />
          Ver búsquedas
        </Link>
      </Button>
    </div>
  );
}

// =================== FILTROS ===================
function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (v: DateRange) => void;
}) {
  const labels: Record<DateRange, string> = {
    all: "Todas",
    today: "Hoy",
    "7d": "7 días",
    "30d": "30 días",
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="default" size="sm" className="h-8 text-xs">
          <Calendar className="h-3 w-3" />
          {labels[value]}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Rango de fechas</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(Object.keys(labels) as DateRange[]).map((k) => (
          <DropdownMenuItem
            key={k}
            onClick={() => onChange(k)}
            className={cn(k === value && "bg-accent")}
          >
            {labels[k]}
            {k === value && <Check className="ml-auto h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ExerciseFilter({
  exercises,
  value,
  onChange,
}: {
  exercises: Pick<Exercise, "id" | "title" | "language">[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }
  const label =
    value.length === 0
      ? "Ejercicios"
      : value.length === 1
        ? exercises.find((e) => e.id === value[0])?.title ?? "1 ejercicio"
        : `${value.length} ejercicios`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="default" size="sm" className="h-8 text-xs max-w-[140px]">
          <Filter className="h-3 w-3" />
          <span className="truncate">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[260px] max-h-[400px] overflow-auto">
        <DropdownMenuLabel>Filtrar por ejercicio</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {exercises.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
            Sin ejercicios cargados.
          </div>
        ) : (
          exercises.map((e) => {
            const checked = value.includes(e.id);
            return (
              <DropdownMenuItem
                key={e.id}
                onSelect={(ev) => {
                  ev.preventDefault();
                  toggle(e.id);
                }}
                className="gap-2"
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(e.id)} />
                <span className="flex-1 truncate text-sm">{e.title}</span>
              </DropdownMenuItem>
            );
          })
        )}
        {value.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onChange([])}>
              <X className="h-3.5 w-3.5" />
              Limpiar selección
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusFilter({
  value,
  onChange,
}: {
  value: AssignmentStatus | "all";
  onChange: (v: AssignmentStatus | "all") => void;
}) {
  const options: { key: AssignmentStatus | "all"; label: string }[] = [
    { key: "all", label: "Todos" },
    { key: "pending", label: "Pendiente" },
    { key: "sent", label: "Enviado" },
    { key: "in_progress", label: "En curso" },
    { key: "completed", label: "Completado" },
    { key: "invalidated", label: "Invalidado" },
    { key: "timed_out", label: "Tiempo agotado" },
    { key: "expired", label: "Expirado" },
  ];
  const current = options.find((o) => o.key === value)?.label ?? "Estado";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="default" size="sm" className="h-8 text-xs">
          <Label className="text-xs cursor-pointer">{current}</Label>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((o) => (
          <DropdownMenuItem
            key={o.key}
            onClick={() => onChange(o.key)}
            className={cn(o.key === value && "bg-accent")}
          >
            {o.label}
            {o.key === value && <Check className="ml-auto h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ApplicationStatusFilter({
  value,
  onChange,
}: {
  value: ApplicationStatus | "all";
  onChange: (v: ApplicationStatus | "all") => void;
}) {
  const options: { key: ApplicationStatus | "all"; label: string }[] = [
    { key: "all", label: "Todos" },
    { key: "new", label: APPLICATION_STATUS_LABELS.new },
    { key: "reviewing", label: APPLICATION_STATUS_LABELS.reviewing },
    { key: "test_sent", label: APPLICATION_STATUS_LABELS.test_sent },
    { key: "hired", label: APPLICATION_STATUS_LABELS.hired },
    { key: "rejected", label: APPLICATION_STATUS_LABELS.rejected },
  ];
  const current = options.find((o) => o.key === value)?.label ?? "Estado";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="default" size="sm" className="h-8 text-xs">
          <Label className="text-xs cursor-pointer">{current}</Label>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((o) => (
          <DropdownMenuItem
            key={o.key}
            onClick={() => onChange(o.key)}
            className={cn(o.key === value && "bg-accent")}
          >
            {o.label}
            {o.key === value && <Check className="ml-auto h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// =================== HELPERS ===================
function sinceFromRange(r: DateRange): Date | null {
  const now = new Date();
  if (r === "all") return null;
  if (r === "today") {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (r === "7d") {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return d;
  }
  if (r === "30d") {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return d;
  }
  return null;
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHrs < 1) {
    const diffMin = Math.floor(diffMs / (1000 * 60));
    return diffMin <= 0 ? "ahora" : `${diffMin}m`;
  }
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "short",
  });
}
